import { defineMessage } from "react-intl"
import { AUTHORIZATIONS_AUTHORIZATION_VALUE_PENALTY_RECORDS_VIEW_ANY } from "./authorizations"

// ============================================================
// Wizard steps
// ============================================================

export const INTERVENTION_STEP_SLOT = 1
export const INTERVENTION_STEP_PHOTOS_BEFORE = 2
export const INTERVENTION_STEP_TASKS = 3
export const INTERVENTION_STEP_PHOTOS_AFTER = 4
export const INTERVENTION_STEP_SIGN = 5
export const INTERVENTION_STEP_CONFIRMATION = 6

/**
 * Ordered list of wizard step ids, from the first (slot selection) to the last
 * (confirmation). The index in this array is the zero-based step position; the
 * value is the step id used throughout the wizard.
 * @type {number[]}
 */
export const INTERVENTION_STEPS = [
  INTERVENTION_STEP_SLOT,
  INTERVENTION_STEP_PHOTOS_BEFORE,
  INTERVENTION_STEP_TASKS,
  INTERVENTION_STEP_PHOTOS_AFTER,
  INTERVENTION_STEP_SIGN,
  INTERVENTION_STEP_CONFIRMATION,
]

export const INTERVENTION_STEP_MESSAGE = defineMessage({
  defaultMessage:
    "{step, select, 1 {Créneau} 2 {Photos avant} 3 {Tâches} 4 {Photos après} 5 {Signer} 6 {Confirmation} other {{step}}}",
})

// ============================================================
// Picture types
// ============================================================

/**
 * Picture type sent on every uploaded intervention photo, mirroring the API's
 * `Picture::TYPE_BEFORE` / `Picture::TYPE_AFTER`. The wizard captures two photo
 * sets — the "Photos avant" step tags its photos `before`, the "Photos après"
 * step tags them `after` — and the finalize normalizer emits the (required)
 * `data[pictures][i][type]` from these values. The type travels at finalize
 * rather than at upload time, which is what lets one queue serve both sets: a
 * staged file is type-agnostic until the intervention claims it.
 * @type {"before"}
 */
export const INTERVENTION_PICTURE_TYPE_BEFORE = "before"
/** @type {"after"} */
export const INTERVENTION_PICTURE_TYPE_AFTER = "after"

// ============================================================
// Comment field
// ============================================================

/**
 * Maximum number of characters accepted in the intervention's free-text comment.
 * Single source of truth shared by the wizard zod schema (the hard gate) and the
 * Sign step's textarea characters counter.
 * @type {number}
 */
export const INTERVENTION_COMMENT_MAX_LENGTH = 2000

// ============================================================
// Occurrence lateness tags
// ============================================================

/** @type {"late"} */
export const INTERVENTION_OCCURRENCE_LATENESS_LATE = "late"
/** @type {"on_time"} */
export const INTERVENTION_OCCURRENCE_LATENESS_ON_TIME = "on_time"
/** @type {"early"} */
export const INTERVENTION_OCCURRENCE_LATENESS_EARLY = "early"

export const INTERVENTION_OCCURRENCE_LATENESS_MESSAGE = defineMessage({
  defaultMessage:
    "{lateness, select, late {Retard} on_time {À l'heure} early {En avance} other {{lateness}}}",
})

/**
 * Chakra `colorPalette` token per occurrence lateness, used to tag each slot in
 * the wizard's Step 1 radio list:
 * - `late` -> orange (overdue, needs attention)
 * - `on_time` -> blue (due now / on schedule)
 * - `early` -> gray (not yet due, low urgency)
 * @type {Record<"late" | "on_time" | "early", string>}
 */
export const INTERVENTION_OCCURRENCE_LATENESS_COLOR_PALETTE = {
  [INTERVENTION_OCCURRENCE_LATENESS_LATE]: "orange",
  [INTERVENTION_OCCURRENCE_LATENESS_ON_TIME]: "blue",
  [INTERVENTION_OCCURRENCE_LATENESS_EARLY]: "gray",
}

// ============================================================
// Helpers
// ============================================================

/**
 * Flatten the server-grouped occurrences (`{ late, on_time, early }`) into a
 * single, flat radio-list array, tagging each item with its `lateness` group so
 * the UI can render the "Retard" badge without doing any client-side date math.
 *
 * The server has already grouped occurrences relative to "now"; this helper only
 * tags and concatenates them. Items are returned in the order: `late` first
 * (the most urgent — overdue interventions surface at the top of the radio list),
 * then `on_time`, then `early`. Within each group the original server order is
 * preserved.
 * @param {{
 *   late?: import("../types/Patrimony").Occurrence[],
 *   on_time?: import("../types/Patrimony").Occurrence[],
 *   early?: import("../types/Patrimony").Occurrence[],
 * } | null | undefined} groupedOccurrences
 * @returns {Array<import("../types/Patrimony").Occurrence & {
 *   lateness: "late" | "on_time" | "early",
 * }>} Flat list of occurrences, each augmented with a `lateness` tag.
 */
export function flattenOccurrences(groupedOccurrences) {
  if (!groupedOccurrences) {
    return []
  }

  const { late = [], on_time = [], early = [] } = groupedOccurrences

  return [
    ...late.map((occurrence) => ({
      ...occurrence,
      lateness: INTERVENTION_OCCURRENCE_LATENESS_LATE,
    })),
    ...on_time.map((occurrence) => ({
      ...occurrence,
      lateness: INTERVENTION_OCCURRENCE_LATENESS_ON_TIME,
    })),
    ...early.map((occurrence) => ({
      ...occurrence,
      lateness: INTERVENTION_OCCURRENCE_LATENESS_EARLY,
    })),
  ]
}

// ============================================================
// Timing status
// ============================================================

export const INTERVENTIONS_TIMING_STATUS_VALUE_EARLY = "early"
export const INTERVENTIONS_TIMING_STATUS_VALUE_ON_TIME = "on_time"
export const INTERVENTIONS_TIMING_STATUS_VALUE_LATE = "late"

/**
 * Non-empty tuple of every intervention timing status. Typed as a tuple so it
 * can seed a `z.enum` (which requires `[U, ...U[]]`).
 * @type {[import("../types/Intervention").InterventionTimingStatus, ...import("../types/Intervention").InterventionTimingStatus[]]}
 */
export const INTERVENTIONS_TIMING_STATUS_VALUES = [
  INTERVENTIONS_TIMING_STATUS_VALUE_EARLY,
  INTERVENTIONS_TIMING_STATUS_VALUE_ON_TIME,
  INTERVENTIONS_TIMING_STATUS_VALUE_LATE,
]

/** @type {Record<string, {id: string, chakraV3ColorPalette: string, icon: string}>} */
export const INTERVENTIONS_TIMING_STATUSES = {
  [INTERVENTIONS_TIMING_STATUS_VALUE_EARLY]: {
    id: INTERVENTIONS_TIMING_STATUS_VALUE_EARLY,
    chakraV3ColorPalette: "gray",
    icon: "ms_schedule",
  },
  [INTERVENTIONS_TIMING_STATUS_VALUE_ON_TIME]: {
    id: INTERVENTIONS_TIMING_STATUS_VALUE_ON_TIME,
    chakraV3ColorPalette: "green",
    icon: "ms_check_circle",
  },
  [INTERVENTIONS_TIMING_STATUS_VALUE_LATE]: {
    id: INTERVENTIONS_TIMING_STATUS_VALUE_LATE,
    chakraV3ColorPalette: "orange",
    icon: "ms_warning",
  },
}

export const INTERVENTIONS_TIMING_STATUS_LIST = Object.values(
  INTERVENTIONS_TIMING_STATUSES,
)

export const INTERVENTIONS_TIMING_STATUS_MESSAGE = defineMessage({
  defaultMessage:
    "{timingStatus, select, early {En avance} on_time {À l'heure} late {En retard} other {{timingStatus}}}",
})

// ============================================================
// Sort
// ============================================================

export const INTERVENTIONS_SORT_VALUE_SCANNED_ASC = "scanned_asc"
export const INTERVENTIONS_SORT_VALUE_SCANNED_DESC = "scanned_desc"
export const INTERVENTIONS_SORT_VALUE_CREATED_ASC = "created_asc"
export const INTERVENTIONS_SORT_VALUE_CREATED_DESC = "created_desc"

/** @type {Record<string, {id: string}>} */
export const INTERVENTIONS_SORTS = {
  [INTERVENTIONS_SORT_VALUE_SCANNED_ASC]: {
    id: INTERVENTIONS_SORT_VALUE_SCANNED_ASC,
  },
  [INTERVENTIONS_SORT_VALUE_SCANNED_DESC]: {
    id: INTERVENTIONS_SORT_VALUE_SCANNED_DESC,
  },
  [INTERVENTIONS_SORT_VALUE_CREATED_ASC]: {
    id: INTERVENTIONS_SORT_VALUE_CREATED_ASC,
  },
  [INTERVENTIONS_SORT_VALUE_CREATED_DESC]: {
    id: INTERVENTIONS_SORT_VALUE_CREATED_DESC,
  },
}

export const INTERVENTIONS_SORT_LIST = Object.values(INTERVENTIONS_SORTS)

export const INTERVENTIONS_SORT_MESSAGE = defineMessage({
  defaultMessage:
    "{sort, select, scanned_asc {Date de scan croissante} scanned_desc {Date de scan décroissante} created_asc {Date de création croissante} created_desc {Date de création décroissante} other {{sort}}}",
})

// ============================================================
// Search tabs
// ============================================================

export const INTERVENTIONS_SEARCH_TABS_MESSAGE = defineMessage({
  defaultMessage: "{tab, select, all {Toutes} trash {Corbeille} other {{tab}}}",
})

/** @type {{value: string, fields: Array<{name: string, value: *}>, defaultFields: Array<{name: string, value: *}>, chakraV3ColorPalette: string | null, countMetaPath: string | null}} */
export const INTERVENTIONS_SEARCH_TABS_VALUE_ALL = {
  value: "",
  fields: [],
  defaultFields: [],
  chakraV3ColorPalette: null,
  countMetaPath: null,
}

/** @type {{value: string, fields: Array<{name: string, value: *}>, defaultFields: Array<{name: string, value: *}>, chakraV3ColorPalette: string | null, countMetaPath: string | null}} */
export const INTERVENTIONS_SEARCH_TABS_VALUE_TRASH = {
  value: "trash",
  fields: [{ name: "only_trashed", value: 1 }],
  defaultFields: [{ name: "only_trashed", value: undefined }],
  chakraV3ColorPalette: "red",
  countMetaPath: null,
}

export const INTERVENTIONS_SEARCH_TABS = [
  INTERVENTIONS_SEARCH_TABS_VALUE_ALL,
  INTERVENTIONS_SEARCH_TABS_VALUE_TRASH,
]

// ============================================================
// View tabs
// ============================================================

export const INTERVENTIONS_VIEW_TAB_VALUE_INFORMATIONS = "informations"
export const INTERVENTIONS_VIEW_TAB_VALUE_PENALTIES = "penalties"

export const INTERVENTIONS_VIEW_TABS_MESSAGE = defineMessage({
  defaultMessage:
    "{tab, select, informations {Informations} penalties {Pénalités} other {{tab}}}",
})

export const INTERVENTIONS_VIEW_TABS = [
  {
    value: INTERVENTIONS_VIEW_TAB_VALUE_INFORMATIONS,
    countMetaPath: null,
    authorization: null,
  },
  {
    value: INTERVENTIONS_VIEW_TAB_VALUE_PENALTIES,
    countMetaPath: "count_penalty",
    authorization: AUTHORIZATIONS_AUTHORIZATION_VALUE_PENALTY_RECORDS_VIEW_ANY,
  },
]

/**
 * Build the `requirements[]` submission payload from the wizard's `checkedItems`
 * state and the schedule `requirements`.
 *
 * Canonical `checkedItems` shape: `Record<number, number[]>` keyed by
 * `requirement_id`, whose value is the array of checked `checklist_item_id`s for
 * that requirement (e.g. `{ 12: [101, 103], 18: [18] }`). A single (non-checklist)
 * task requirement is modelled as a one-pseudo-item bucket: when checked, its
 * bucket holds the requirement's own id (`[requirement.id]`); when unchecked it is
 * empty or absent. This unifies both shapes — every bucket is just "checked ids".
 *
 * Rules:
 * - Send only checked checklist items.
 * - Skip a requirement entirely if it has no checked items.
 * - Still send a requirement if its single (non-checklist) task is checked; in
 *   that case `items` is an empty array.
 *
 * What omission MEANS: the wizard starts with nothing checked (issue #877), so the
 * payload carries exactly what the provider declared having done. The server
 * snapshots every provider-visible requirement of the schedule and derives
 * compliance from presence here — an omitted requirement (or a checklist item
 * missing from a present one) is recorded NON-compliant, and a non-compliant
 * checklist item carrying a penalty amount creates a penalty record. Omission is
 * therefore a declaration, never a "no data" placeholder.
 *
 * Pure and side-effect free — also reused by the submit normalizer (#15).
 * @param {Record<number, number[]> | null | undefined} checkedItems
 *   Map keyed by `requirement_id` -> array of checked `checklist_item_id`s.
 * @param {import("../types/Patrimony").Requirement[] | null | undefined} requirements
 * @returns {Array<{
 *   requirement_id: number,
 *   items: Array<{ checklist_item_id: number }>,
 * }>} Payload containing only requirements with at least one checked item.
 */
export function buildCheckedRequirementsPayload(checkedItems, requirements) {
  if (!checkedItems || !requirements) {
    return []
  }

  const payload = []

  for (const requirement of requirements) {
    const checkedIds = checkedItems[requirement.id] ?? []

    if (requirement.has_checklist) {
      const checklistItems = requirement.checklist_items ?? []
      const items = checklistItems
        .filter((checklistItem) => checkedIds.includes(checklistItem.id))
        .map((checklistItem) => ({ checklist_item_id: checklistItem.id }))

      // Skip the requirement entirely when none of its checklist items are checked.
      if (items.length === 0) {
        continue
      }

      payload.push({ requirement_id: requirement.id, items })
    } else {
      // Single-task requirement: checked when its one-pseudo-item bucket is
      // non-empty (holds the requirement id). No checklist items to send.
      if (checkedIds.length === 0) {
        continue
      }

      payload.push({ requirement_id: requirement.id, items: [] })
    }
  }

  return payload
}
